import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { Plus, FileText, Search, ExternalLink, Calendar, MapPin, Trash2, Edit2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function MBDashboard() {
  const navigate = useNavigate();
  const [mbList, setMbList] = useState([]);
  const [loading, setLoading] = useState(true);

  // States for Inline Editing Modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMb, setEditingMb] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchMBs();
  }, []);

  async function fetchMBs() {
    const { data, error } = await supabase
      .from("measurement_bills")
      .select(`*, sites(site_name)`)
      .order("created_at", { ascending: false });
    if (data) setMbList(data);
    setLoading(false);
  }

  // Open modal and pre-fill form data
  function openEditModal(mb) {
    setEditingMb({ ...mb });
    setIsEditModalOpen(true);
  }

  // Handle saving the edited data back to Supabase
  async function handleUpdate(e) {
    e.preventDefault();
    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("measurement_bills")
        .update({
          bill_no: editingMb.bill_no,
          date: editingMb.date,
          po_number: editingMb.po_number,
          total_amount: parseFloat(editingMb.total_amount) || 0
        })
        .eq("id", editingMb.id);

      if (error) {
        alert("Error updating record: " + error.message);
      } else {
        // Update local state smoothly
        setMbList(mbList.map((mb) => (mb.id === editingMb.id ? { ...mb, ...editingMb } : mb)));
        setIsEditModalOpen(false);
        setEditingMb(null);
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("An unexpected error occurred.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(id) {
    const confirmed = window.confirm("Are you sure you want to delete this Measurement Book record?");
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from("measurement_bills")
        .delete()
        .eq("id", id);

      if (error) {
        alert("Error deleting record: " + error.message);
      } else {
        setMbList(mbList.filter((mb) => mb.id !== id));
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("An unexpected error occurred.");
    }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-8 relative">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-end mb-8">
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">MEASUREMENT BOOKS</h1>
            <p className="text-slate-500 font-medium">Manage and track site project measurements</p>
          </div>
          <button
            onClick={() => navigate("/mb/create")}
            className="bg-[#0054A6] hover:bg-blue-700 text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2 shadow-lg shadow-blue-200 transition-all active:scale-95"
          >
            <Plus size={20} /> CREATE NEW MB
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
            <p className="text-slate-400 text-xs font-black uppercase tracking-widest mb-1">Total Records</p>
            <p className="text-2xl font-black text-slate-900">{mbList.length}</p>
          </div>
        </div>

        {/* List Table */}
        <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-8 py-5">Date / Bill No</th>
                <th className="px-6 py-5">Site / PO Number</th>
                <th className="px-6 py-5 text-right">Total Amount</th>
                <th className="px-8 py-5 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {mbList.map((mb) => (
                <tr key={mb.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-8 py-5">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-900">{mb.bill_no}</span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar size={12} /> {mb.date}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-700 flex items-center gap-1">
                        <MapPin size={12} className="text-blue-500" /> {mb.sites?.site_name}
                      </span>
                      <span className="text-[10px] font-black text-slate-400 uppercase">PO: {mb.po_number || "N/A"}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <span className="font-black text-[#0054A6]">₹{mb.total_amount?.toLocaleString("en-IN")}</span>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center justify-center gap-4">
                      {/* View Button */}
                      <button 
                        onClick={() => navigate(`/mb/view/${mb.id}`)}
                        className="text-slate-400 hover:text-blue-600 transition-colors"
                        title="View Details"
                      >
                        <ExternalLink size={18} />
                      </button>

                      {/* Edit Button - Opens Inline Modal */}
                    <button 
                        onClick={() => navigate(`/mb/edit/${mb.id}`)}
                        className="text-slate-400 hover:text-amber-500 transition-colors"
                        title="Edit Full Details"
                      >
                        <Edit2 size={16} />
                      </button>
                      
                      {/* Delete Button */}
                      <button 
                        onClick={() => handleDelete(mb.id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        title="Delete Record"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {mbList.length === 0 && !loading && (
            <div className="p-20 text-center text-slate-400 font-medium">No records found. Click "Create New MB" to start.</div>
          )}
        </div>
      </div>

      {/* --- INLINE EDIT MODAL OVERLAY --- */}
      {isEditModalOpen && editingMb && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-[2rem] shadow-2xl border border-slate-100 max-w-md w-full overflow-hidden transform scale-100 transition-all">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center px-8 py-6 border-b border-slate-100 bg-slate-50/50">
              <div>
                <h3 className="text-xl font-black text-slate-900 tracking-tight">EDIT RECORD</h3>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-0.5">{editingMb.sites?.site_name}</p>
              </div>
              <button 
                onClick={() => setIsEditModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 bg-white p-2 rounded-full border border-slate-100 shadow-sm transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleUpdate} className="p-8 space-y-5">
              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2">Bill Number</label>
                <input
                  type="text"
                  required
                  value={editingMb.bill_no || ""}
                  onChange={(e) => setEditingMb({ ...editingMb, bill_no: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2">Date</label>
                <input
                  type="date"
                  required
                  value={editingMb.date || ""}
                  onChange={(e) => setEditingMb({ ...editingMb, date: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2">PO Number</label>
                <input
                  type="text"
                  value={editingMb.po_number || ""}
                  onChange={(e) => setEditingMb({ ...editingMb, po_number: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2">Total Amount (₹)</label>
                <input
                  type="number"
                  required
                  value={editingMb.total_amount || ""}
                  onChange={(e) => setEditingMb({ ...editingMb, total_amount: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-black text-[#0054A6] text-lg focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                />
              </div>

              {/* Form Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-5 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-3 bg-[#0054A6] hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-100 disabled:opacity-50"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </div>
  );
}